<script lang="ts">
import { VueDraggableNext } from 'vue-draggable-next'
import { v4 as uuidv4 } from 'uuid'

export default defineComponent({
  components: {
    draggable: VueDraggableNext,
  },
})
</script>
<script setup lang="ts">
import { Form, Field, ErrorMessage } from 'vee-validate'
import * as yup from 'yup'
import RichTextEditor from '~/components/Form/RichTextEditor.vue'

const store = usePrestacaoDeContaStore()
const { modalLancarDespesa } = storeToRefs(store)
const arquivos = ref([])
const emit = defineEmits(['despesa-salva'])
const larkStore = useLarkStore()

// Data atual no formato YYYY-MM-DD para limitar o datepicker
const dataAtual = computed(() => {
  const hoje = new Date()
  return hoje.toISOString().split('T')[0]
})

onMounted(async () => {
  await store.fetchCategorias()
  await store.fetchContas()
})

// Customização do método yup para números formatados
yup.addMethod(yup.number, 'formatted', function (message) {
  return this.transform((value, originalValue) => {
    console.log(value)
    const valorFormatado = originalValue ? Number(originalValue?.replace('.', '').replace(',', '.')) : originalValue
    modalLancarDespesa.value.valor = valorFormatado
    return valorFormatado
  }).typeError(message)
})

// Validação usando Yup
const schema = yup.object({
  // descricao: yup.string().required('Descrição é obrigatória'),
  conta_id: yup.string().required('Categoria é obrigatória'),
  valor: yup.number().formatted('Valor deve ser um número válido').required('Valor é obrigatório').min(0, 'Valor não pode ser negativo'),
  vencimento: yup.date()
    .typeError('Data de vencimento inválida')
    .required('Data de vencimento é obrigatória'),
  arquivos: yup.array().of(yup.object().nullable()),
})

function cancelar () {
  arquivos.value = []
  store.resetarModalLancarDespesa()
}

function configurarRecorrencia () {
  const values = { ...modalLancarDespesa.value }
  store.modalConfigurarRecorrencia.despesaBase = values
  useStore().substituirModal(store.modalConfigurarRecorrencia, modalLancarDespesa.value)
}

const handleSubmit = async (values) => {
  try {
    useStore().loading = true
    let despesas

    if (store.modalLancarDespesa.recorrencias.length > 0) {
      const recorrenciaId = uuidv4()
      // Use compiled individual files from modalConfigurarRecorrencia.arquivosIndividuais
      const arquivosCompilados = store.modalConfigurarRecorrencia.arquivosIndividuais || {}
      const totalParcelas = store.modalLancarDespesa.recorrencias.length
      despesas = store.modalLancarDespesa.recorrencias.map((data, index) => ({
        ...modalLancarDespesa.value, // Use the full modal state instead of form values
        vencimento: data,
        recorrencia_id: recorrenciaId,
        numero_parcela: index + 1,
        total_parcelas: totalParcelas,
        // Use compiled files for this specific installment, plus main form files for first expense
        arquivos: index === 0
          ? [...arquivos.value, ...(arquivosCompilados[index] || [])]
          : (arquivosCompilados[index] || []),
      }))
    } else {
      despesas = [{
        ...modalLancarDespesa.value, // Use the full modal state
        dataDoPagamento: values.dataDoPagamento,
        arquivos: arquivos.value,
      }]
    }

    // Verificar se alguma das despesas já existe
    const duplicatasPorDespesa = await store.verificarTodasDespesasDuplicadas(despesas)

    if (duplicatasPorDespesa.length > 0) {
      // Se há duplicatas, usar substituirModal para alternar
      store.modalConfirmarDespesaDuplicada = {
        exibir: false,
        despesasParaSalvar: despesas,
        duplicatasEncontradas: duplicatasPorDespesa,
        callback: async () => {
          // Esta função será chamada se o usuário confirmar
          await salvarDespesasConfirmado(despesas)
        },
      }
      useStore().substituirModal(store.modalConfirmarDespesaDuplicada, modalLancarDespesa.value)
      return // Interrompe o fluxo, aguarda decisão do usuário
    }

    // Se não há duplicatas, prossegue normalmente
    await salvarDespesasConfirmado(despesas)
  } catch (err) {
    let errorMessage = 'Erro ao salvar despesa'
    if (err.message) {
      errorMessage = err.message
    }
    useStore().toasts.push({ content: errorMessage, color: 'danger' })
    console.error(err)
  } finally {
    useStore().loading = false
  }
}

// Função separada para salvar as despesas (usada tanto no fluxo normal quanto após confirmação)
const salvarDespesasConfirmado = async (despesas: any) => {
  const resultado = await store.salvarDespesa(despesas)
  store.despesas.push(...(Array.isArray(resultado) ? resultado : [resultado]))
  store.resetarModalLancarDespesa()
  store.resetarModalConfigurarRecorrencia()
  arquivos.value = []
  emit('despesa-salva')
}

function contasAgrupadasPorCategoria () {
  const categorias = store.contas.reduce((acc: string[], conta: { categoria: string }) => {
    if (!acc.includes(conta.categoria)) {
      acc.push(conta.categoria)
    }
    return acc
  }, [])

  const contasAgrupadas = categorias.map((categoria) => {
    return {
      label: categoria,
      options: store.contas.filter(conta => conta.categoria === categoria).map(conta => ({ value: conta.id, label: conta.nome })),
    }
  })

  return contasAgrupadas
}

function atualizarArquivos (files) {
  arquivos.value = files.map(file => file.file)
}

// Add new computed property for recurrence info
const recorrenciaInfo = computed(() => {
  const recorrencias = store.modalLancarDespesa.recorrencias
  if (!recorrencias?.length) { return null }

  const tipo = store.modalConfigurarRecorrencia.tipo
  const quantidade = recorrencias.length
  const periodo = tipo === 'MENSAL'
    ? quantidade > 1 ? 'meses' : 'mês'
    : quantidade > 1 ? 'semanas' : 'semana'

  return {
    texto: `Esta despesa será repetida por ${quantidade} ${periodo}`,
    temRecorrencia: true,
  }
})

function cancelarRecorrencia () {
  store.modalLancarDespesa.recorrencias = []
  store.modalLancarDespesa.vencimento = new Date().toISOString().split('T')[0]
}

// Add this function to format file sizes
function formatFileSize (bytes: number): string {
  if (bytes === 0) { return '0 Bytes' }
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Add this function to remove files
function removerArquivo (index: number) {
  arquivos.value.splice(index, 1)
}

// Adicione este computed property
const larkTooltip = computed(() => {
  if (!larkStore.isAuthenticated) {
    return 'Conecte-se ao Lark para criar lembretes'
  }
  if (!larkStore.calendarId) {
    return 'Selecione um calendário padrão nas configurações do Lark'
  }
  return 'Marque se deseja ser lembrado no calendário do Lark'
})

const canCreateReminder = computed(() => {
  return larkStore.isAuthenticated && !!larkStore.calendarId
})

const contaDespesaEfetiva = computed(() => {
  const conta = store.contas.find((c: any) => c.id === modalLancarDespesa.value.conta_id)
  return conta?.despesa_efetiva === true
})

</script>

<template>
  <div>
    <CModal
      id="modalLancarDespesa"
      :visible="modalLancarDespesa.exibir"
      size="xl"
      backdrop="static"
      @close="cancelar"
    >
      <CModalHeader>
        <CModalTitle id="lancarDespesa">
          Lançar Despesa
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        <Form :validation-schema="schema" @submit="handleSubmit">
          <CRow class="mb-3">
            <CCol :md="12">
              <!-- Conta -->
              <div class="mb-2">
                <label class="form-label" for="conta">
                  Categoria
                </label>
                <Field v-slot="{ field, meta }" v-model="modalLancarDespesa.conta_id" name="conta_id">
                  <Multiselect
                    v-bind="field"
                    :options="contasAgrupadasPorCategoria()"
                    :searchable="true"
                    :create-option="false"
                    placeholder="Selecione ou pesquise"
                    :class="useStore().retornarClasseDeValidacao(meta)"
                    :groups="true"
                    @change="() => {
                      if (meta.touched && meta.valid) {
                        modalLancarDespesa.despesaNaoEfetiva = !contaDespesaEfetiva
                      }
                    }"
                  />
                </Field>
                <ErrorMessage name="conta_id" class="invalid-feedback" />
              </div>
            </CCol>
            <CCol :md="12">
              <!-- Conta -->
              <div v-if="contaDespesaEfetiva" class="mb-2">
                <label v-c-tooltip="'Considera-se despesa indireta aquela lançada exclusivamente para fins de Livro Caixa da Receita Federal, necessária à percepção da receita e à manutenção da fonte produtora.'" class="form-label" for="checkboxDespesaEfetiva">
                  <input id="checkboxDespesaEfetiva" v-model="modalLancarDespesa.despesaNaoEfetiva" type="checkbox" class="form-check-input">
                  Não é despesa direta
                </label>
              </div>
              <div v-else-if="modalLancarDespesa.conta_id" class="alert alert-info d-flex align-items-center gap-2 p-2 rounded">
                <CIcon icon="cil-info" class="ms-1" />
                <span class="">
                  Esta despesa será lançada como despesa INDIRETA
                </span>
              </div>
            </CCol>

            <CCol :md="12">
              <div class="mb-2">
                <label class="form-label" for="categoria">
                  Descrição (Histórico)
                </label>
                <Field v-slot="{ field, meta }" v-model="modalLancarDespesa.descricao" name="descricao">
                  <RichTextEditor
                    :model-value="modalLancarDespesa.descricao"
                    :class="useStore().retornarClasseDeValidacao(meta)"
                    @update:model-value="val => { modalLancarDespesa.descricao = val; field.onChange(val); }"
                  />
                </Field>
                <ErrorMessage name="descricao" class="invalid-feedback" />
              </div>
            </CCol>

            <CCol :md="6">
              <div class="mb-2">
                <label class="form-label" for="valor">
                  Valor
                </label>
                <Field v-slot="{ field, meta }" v-model="store.modalConfigurarRecorrencia.valor" name="valor">
                  <input
                    id="valor"
                    v-bind="field"
                    v-maska
                    type="text"
                    label="Valor"
                    data-maska="#.###.###.###,##"
                    data-maska-reversed
                    class="form-control"
                    :class="useStore().retornarClasseDeValidacao(meta)"
                    required
                  >
                </Field>
                <ErrorMessage name="valor" class="invalid-feedback" />
              </div>
            </CCol>

            <CCol :md="6">
              <!-- Vencimento -->
              <div class="mb-2">
                <label class="form-label" for="dataParaEfetuarPagamento">
                  Vencimento
                </label>
                <Field v-slot="{ meta }" v-model="modalLancarDespesa.vencimento" name="vencimento">
                  <input
                    :value="modalLancarDespesa.vencimento"
                    type="date"
                    class="form-control"
                    :class="useStore().retornarClasseDeValidacao(meta)"
                    :disabled="modalLancarDespesa.recorrencias.length > 0"
                    @input="e => modalLancarDespesa.vencimento = e.target.value"
                  >
                </Field>
                <ErrorMessage name="vencimento" class="invalid-feedback" />
                <div v-if="recorrenciaInfo?.temRecorrencia" class="d-flex align-items-center gap-2 mt-1">
                  <small class="text-info">{{ recorrenciaInfo.texto }}</small>
                  <CButton
                    size="sm"
                    color="danger"
                    variant="ghost"
                    @click="cancelarRecorrencia"
                  >
                    Cancelar Recorrência
                  </CButton>
                </div>
              </div>
            </CCol>

            <CCol :md="6">
              <!-- Data do Pagamento -->
              <div class="mb-2">
                <label class="form-label" for="dataDoPagamento">
                  Data do Pagamento
                </label>
                <Field v-slot="{ field }" name="dataDoPagamento">
                  <input v-bind="field" type="date" class="form-control" :max="dataAtual">
                </Field>
              </div>
            </CCol>
          </CRow>
          <FilePond
            ref="pond"
            name="file"
            :files="arquivos"
            :label-idle="'Arraste e solte seus arquivos ou <u>clique aqui</u>'"
            :allow-multiple="true"
            :accepted-file-types="['application/pdf', '*']"
            @updatefiles="(arquivos) => atualizarArquivos(arquivos)"
          />
          <ul id="listaArquivos">
            <draggable :list="arquivos" handle=".drag-handle">
              <li
                v-for="(arquivo, index) in arquivos"
                :key="index"
                class="arquivo-item"
              >
                <div class="arquivo-info">
                  <CIcon
                    icon="cil-hamburger-menu"
                    size="sm"
                    class="drag-handle"
                  />
                  <span class="arquivo-nome">{{ arquivo.name }}</span>
                  <span class="arquivo-tamanho">{{ formatFileSize(arquivo.size) }}</span>
                </div>
                <button class="btn-remover" @click="removerArquivo(index)">
                  <CIcon icon="cil-x" size="sm" />
                </button>
              </li>
            </draggable>
          </ul>

          <!-- Adicionar lembrete -->
          <div class="mb-3">
            <div
              v-c-tooltip="larkTooltip"
              class="form-check d-flex align-items-center gap-2 p-2 rounded hover-highlight"
            >
              <input
                id="criarLembrete"
                v-model="modalLancarDespesa.criarLembrete"
                type="checkbox"
                class="cursor-pointer"
                :disabled="!canCreateReminder"
              >
              <label
                class="form-check-label d-flex align-items-center gap-2"
                :class="{
                  'cursor-pointer': canCreateReminder,
                  'text-muted': !canCreateReminder
                }"
                for="criarLembrete"
              >
                <CIcon
                  icon="cil-bell"
                  size="lg"
                  :class="[
                    modalLancarDespesa.criarLembrete && canCreateReminder ? 'text-primary' : 'text-secondary',
                    !canCreateReminder ? 'opacity-50' : ''
                  ]"
                />
                <span>Criar lembrete no dia do vencimento</span>
              </label>
            </div>
          </div>

          <!-- Observação -->
          <!-- <div class="my-3">
            <label class="form-label" for="observacao">
              Observação
            </label>
            <textarea id="observacao" v-model="modalLancarDespesa.observacao" class="form-control" />
          </div> -->

          <CModalFooter>
            <span class="btn btn-secondary" @click="configurarRecorrencia">
              Configurar Recorrência
            </span>
            <CButton color="secondary" @click="cancelar">
              Cancelar
            </CButton>
            <CButton type="submit" color="primary">
              Salvar
            </CButton>
          </CModalFooter>
        </Form>
      </CModalBody>
    </CModal>
  </div>
</template>

<style scoped>
.hover-highlight:hover {
  background-color: #f8f9fa;
}
.cursor-pointer {
  cursor: pointer;
}

#listaArquivos {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 0.5rem;
  margin-top: 0.5rem;
  list-style: none;
}

.arquivo-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  margin-bottom: 0.25rem;
  background-color: white;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.arquivo-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.arquivo-nome {
  font-size: 0.875rem;
}

.arquivo-tamanho {
  color: #6c757d;
  font-size: 0.75rem;
}

.btn-remover {
  background: none;
  border: none;
  color: #dc3545;
  padding: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-remover:hover {
  color: #bb2d3b;
}

:deep(.filepond--root){
  margin-bottom: 0;
}

:deep(.filepond--item){
  display: none !important;
}

.drag-handle {
  cursor: grab;
  color: #6c757d;
  margin-right: 0.5rem;
}

.drag-handle:active {
  cursor: grabbing;
}
:deep(.ck-content) { height:120px; }

</style>
